import {
  AssignOrgTeacherToActivityScheduleMutation,
  AssignOrgTeacherToActivityScheduleMutationVariables,
  SellerOrgAvailabilityQuery,
  SellerOrgAvailabilityQueryVariables,
  UnassignOrgTeacherToActivityScheduleMutation,
  UnassignOrgTeacherToActivityScheduleMutationVariables,
} from "@outschool/gql-frontend-generated";
import {
  DataProxy,
  ExecutionResult,
  gql,
  useMutation,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import React from "react";

export const sellerOrgAvailabilityScheduleSettingsFragment = gql`
  fragment SellerOrgAvailabilityScheduleSettings on ScheduleSettings {
    uid
    teacher {
      uid
      name
    }
  }
`;

export const sellerOrgAvailabilityQuery = gql`
  query SellerOrgAvailability($uid: ID!) {
    activity(uid: $uid) {
      uid
      scheduleSettings(orgTeachersOnly: true) {
        ...SellerOrgAvailabilityScheduleSettings
      }
    }
  }
  ${sellerOrgAvailabilityScheduleSettingsFragment}
`;

const assignOrgTeacherToActivityScheduleMutation = gql`
  mutation AssignOrgTeacherToActivitySchedule(
    $orgTeacherUid: String!
    $activityUid: String!
  ) {
    assignOrgTeacherToActivitySchedule(
      orgTeacherUid: $orgTeacherUid
      activityUid: $activityUid
    ) {
      ...SellerOrgAvailabilityScheduleSettings
    }
  }
  ${sellerOrgAvailabilityScheduleSettingsFragment}
`;

const unassignOrgTeacherToActivityScheduleMutation = gql`
  mutation UnassignOrgTeacherToActivitySchedule(
    $orgTeacherUid: String!
    $activityUid: String!
  ) {
    unassignOrgTeacherToActivitySchedule(
      orgTeacherUid: $orgTeacherUid
      activityUid: $activityUid
    ) {
      ...SellerOrgAvailabilityScheduleSettings
    }
  }
  ${sellerOrgAvailabilityScheduleSettingsFragment}
`;

type ScheduleSettings = NonNullable<
  SellerOrgAvailabilityQuery["activity"]["scheduleSettings"]
>[number];

function updateSellerOrgTeacherScheduleCache({
  activityUid,
  cache,
  scheduleSettings,
  shouldDelete,
}: {
  activityUid: string;
  cache: DataProxy;
  scheduleSettings?: ScheduleSettings;
  shouldDelete?: boolean;
}) {
  const data = cache.readQuery<
    SellerOrgAvailabilityQuery,
    SellerOrgAvailabilityQueryVariables
  >({
    query: sellerOrgAvailabilityQuery,
    variables: { uid: activityUid },
  });
  const existingScheduleSettings = data?.activity.scheduleSettings ?? [];
  let newScheduleSettings = [...existingScheduleSettings];
  if (!!scheduleSettings) {
    if (shouldDelete) {
      newScheduleSettings = newScheduleSettings.filter(
        nss => nss.uid !== scheduleSettings.uid,
      );
    } else {
      newScheduleSettings.push(scheduleSettings);
    }
  }
  if (!!data?.activity) {
    const newActivity = {
      ...data?.activity,
      scheduleSettings: newScheduleSettings,
    };
    cache.writeQuery<
      SellerOrgAvailabilityQuery,
      SellerOrgAvailabilityQueryVariables
    >({
      query: sellerOrgAvailabilityQuery,
      data: { activity: newActivity },
      variables: { uid: activityUid },
    });
  }
}

export function useGetSellerOrgTeacherScheduleSettings(activityUid: string) {
  const { loading, data } = useQueryWithPreviousData<
    SellerOrgAvailabilityQuery,
    SellerOrgAvailabilityQueryVariables
  >(sellerOrgAvailabilityQuery, {
    skip: !activityUid,
    variables: {
      uid: activityUid!,
    },
  });

  return {
    loading,
    scheduleSettings: data?.activity.scheduleSettings ?? [],
  };
}

export function useHandleAssignOrgTeacher(activityUid: string) {
  const [assignOrgTeacherToActivitySchedule] = useMutation<
    AssignOrgTeacherToActivityScheduleMutation,
    AssignOrgTeacherToActivityScheduleMutationVariables
  >(assignOrgTeacherToActivityScheduleMutation, {
    update: (
      cache,
      result: ExecutionResult<AssignOrgTeacherToActivityScheduleMutation>,
    ) =>
      updateSellerOrgTeacherScheduleCache({
        cache,
        scheduleSettings: result?.data?.assignOrgTeacherToActivitySchedule,
        shouldDelete: false,
        activityUid,
      }),
  });

  return React.useCallback(
    async (orgTeacherUid: string) => {
      if (activityUid) {
        await assignOrgTeacherToActivitySchedule({
          variables: { activityUid, orgTeacherUid },
        });
      }
    },
    [activityUid, assignOrgTeacherToActivitySchedule],
  );
}

export function useHandleUnassignOrgTeacher(activityUid: string) {
  const [unassignOrgTeacherToActivitySchedule] = useMutation<
    UnassignOrgTeacherToActivityScheduleMutation,
    UnassignOrgTeacherToActivityScheduleMutationVariables
  >(unassignOrgTeacherToActivityScheduleMutation, {
    update: (
      cache,
      result: ExecutionResult<UnassignOrgTeacherToActivityScheduleMutation>,
    ) =>
      updateSellerOrgTeacherScheduleCache({
        cache,
        scheduleSettings: result?.data?.unassignOrgTeacherToActivitySchedule,
        shouldDelete: true,
        activityUid,
      }),
  });

  return React.useCallback(
    async (orgTeacherUid: string) => {
      if (activityUid) {
        await unassignOrgTeacherToActivitySchedule({
          variables: { activityUid, orgTeacherUid },
        });
      }
    },
    [activityUid, unassignOrgTeacherToActivitySchedule],
  );
}
